import { useState, FormEvent } from 'react';
import { Layout } from '@/components/feature/Layout';
import SEOMeta from '@/components/feature/SEOMeta';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { stats } from '@/mocks/addresses';

export default function AboutPage() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormState('success');
      return;
    }

    setFormState('submitting');
    setFormError('');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9qb54knlsngrm4llv2g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormState('success');
        form.reset();
      } else {
        const serverMsg =
          parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败';
        if (serverMsg.toLowerCase().includes('spam') || serverMsg.includes('form data is spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(serverMsg);
        }
        setFormState('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormState('error');
    }
  };

  const teamValues = [
    {
      icon: 'ri-shield-star-line',
      title: '安全可靠',
      desc: '采用业内领先的安全防护体系，确保所有地址入口安全可靠，杜绝钓鱼风险。',
    },
    {
      icon: 'ri-speed-up-line',
      title: '极速响应',
      desc: '全球CDN加速节点覆盖，智能路由算法确保您始终连接到最优节点。',
    },
    {
      icon: 'ri-customer-service-2-line',
      title: '贴心服务',
      desc: '7x24小时技术支持，任何问题都能在最短时间内得到响应和解决。',
    },
    {
      icon: 'ri-refresh-line',
      title: '持续更新',
      desc: '地址信息实时更新，多渠道通知推送，确保您第一时间获取最新地址。',
    },
  ];

  const faqs = [
    {
      q: '如何确认当前访问的是官方地址发布页？',
      a: '请检查浏览器地址栏，确保域名与官方公布的域名完全一致。您也可以通过本页面的官方入口跳转访问。如有疑问，可通过下方联系表单与我们确认。',
    },
    {
      q: '地址发布页需要注册账号才能使用吗？',
      a: '不需要。地址发布页对所有用户免费开放，无需注册即可查看所有官方地址入口和使用教程。',
    },
    {
      q: '主域名无法访问时怎么办？',
      a: '建议依次尝试区域备用节点（华东、华南、华北、西南），选择距离您最近的节点。如所有节点均无法访问，请通过邮件联系我们获取最新地址。',
    },
    {
      q: '地址变更后会通过什么方式通知？',
      a: '我们会通过邮件订阅、浏览器通知和本站公告三种方式同步推送。建议您订阅邮件通知，并在浏览器中允许本站的通知权限。',
    },
    {
      q: '如何举报可疑的钓鱼网站？',
      a: '如发现仿冒或钓鱼网站，请通过下方联系表单提交详细信息（包括可疑网址、截图等），我们将在核实后采取相应措施。',
    },
  ];

  return (
    <Layout>
      <SEOMeta
        title="关于我们 - 地址发布页 | 品牌介绍 口碑信誉 联系方式"
        description="地址发布页官方品牌介绍。了解我们的使命与价值观，查看用户口碑与信誉评价，获取最新联系方式。官方唯一指定地址入口导航平台。"
        keywords="关于我们,地址发布页,品牌介绍,口碑信誉,联系方式,官方地址入口"
        canonicalUrl="https://gtucxr.cn/about"
        ogType="website"
      />

      <div className="pt-16">
        <div className="bg-background-100 border-b border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '关于我们' },
              ]}
            />
            <div className="pb-8">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                关于我们
              </h1>
              <p className="text-sm text-foreground-600 max-w-2xl">
                官方唯一指定的地址入口导航平台，致力于为用户提供稳定、安全、便捷的地址导航服务
              </p>
            </div>
          </div>
        </div>

        <section className="py-14 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-5">
                  我们的使命
                </h2>
                <div className="space-y-4 text-sm text-foreground-700 leading-relaxed">
                  <p>
                    在互联网环境中，访问地址的稳定性和安全性至关重要。地址发布页作为官方唯一指定的地址入口导航平台，承担着为所有用户提供可靠访问入口的重要使命。
                  </p>
                  <p>
                    自上线以来，我们已累计服务超过
                    <strong className="text-primary-600">{(stats.usersServed / 10000).toFixed(1)}万</strong>
                    用户，稳定运行
                    <strong className="text-primary-600">{stats.onlineDays}</strong>
                    天，服务可用率保持在
                    <strong className="text-primary-600">{stats.uptime}</strong>
                    。这些数字背后，是团队对稳定性和安全性的不懈追求。
                  </p>
                  <p>
                    我们深知，每一次地址变更都可能给用户带来不便。因此，我们建立了完善的地址监控和通知体系，确保在地址发生变更时，用户能够在第一时间获取最新信息。
                  </p>
                </div>
              </div>
              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20workspace%20with%20warm%20golden%20lighting%2C%20clean%20desk%20setup%20with%20monitor%20and%20keyboard%2C%20cozy%20atmosphere%2C%20warm%20amber%20and%20cream%20color%20scheme%2C%20professional%20yet%20inviting%2C%20soft%20sunlight%20through%20window%2C%20no%20people%2C%20high%20detail%2C%20editorial%20style&width=800&height=600&seq=about-mission-2026&orientation=landscape"
                  alt="地址发布页团队工作环境"
                  className="w-full rounded-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                核心优势
              </h2>
              <p className="text-sm text-foreground-600 max-w-lg mx-auto">
                我们始终坚持以用户体验为核心，不断优化服务品质
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {teamValues.map((value, idx) => (
                <div
                  key={idx}
                  className="bg-background-50 rounded-xl border border-background-200 p-6 text-center hover:border-primary-200 transition-all duration-300"
                >
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 bg-primary-100 rounded-xl">
                    <i className={`${value.icon} text-xl text-primary-600`}></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-2">
                    {value.title}
                  </h3>
                  <p className="text-xs text-foreground-600 leading-relaxed">
                    {value.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-14">
              <div className="bg-background-100 rounded-xl p-6 text-center">
                <p className="text-3xl font-heading font-bold text-primary-500 mb-1">
                  {(stats.totalVisits / 10000).toFixed(0)}万+
                </p>
                <p className="text-sm text-foreground-600">累计访问量</p>
              </div>
              <div className="bg-background-100 rounded-xl p-6 text-center">
                <p className="text-3xl font-heading font-bold text-accent-500 mb-1">
                  {(stats.usersServed / 10000).toFixed(1)}万+
                </p>
                <p className="text-sm text-foreground-600">服务用户数</p>
              </div>
              <div className="bg-background-100 rounded-xl p-6 text-center">
                <p className="text-3xl font-heading font-bold text-primary-500 mb-1">
                  {stats.uptime}
                </p>
                <p className="text-sm text-foreground-600">服务可用率</p>
              </div>
            </div>

            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                常见问题
              </h2>
              <p className="text-sm text-foreground-600 max-w-lg mx-auto">
                以下是用户最常咨询的问题，希望能帮助到您
              </p>
            </div>

            <div className="max-w-3xl mx-auto space-y-4">
              {faqs.map((faq, idx) => (
                <details
                  key={idx}
                  className="bg-background-100 rounded-xl border border-background-200 group cursor-pointer"
                >
                  <summary className="px-5 py-4 font-heading font-medium text-foreground-900 text-sm flex items-center justify-between list-none">
                    {faq.q}
                    <i className="ri-add-line text-foreground-400 group-open:hidden"></i>
                    <i className="ri-subtract-line text-foreground-400 hidden group-open:block"></i>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-16 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                联系我们
              </h2>
              <p className="text-sm text-foreground-600 max-w-lg mx-auto">
                如有任何问题或建议，欢迎通过以下方式与我们联系
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-10">
              <div className="bg-background-50 rounded-xl border border-background-200 p-5 text-center">
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 bg-accent-100 rounded-lg">
                  <i className="ri-mail-line text-lg text-accent-600"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-1">
                  电子邮件
                </h3>
                <p className="text-xs text-foreground-500">
                  support@official-site.com
                </p>
              </div>
              <div className="bg-background-50 rounded-xl border border-background-200 p-5 text-center">
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 bg-primary-100 rounded-lg">
                  <i className="ri-wechat-line text-lg text-primary-600"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-1">
                  微信公众号
                </h3>
                <p className="text-xs text-foreground-500">
                  地址发布页
                </p>
              </div>
              <div className="bg-background-50 rounded-xl border border-background-200 p-5 text-center">
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 bg-secondary-100 rounded-lg">
                  <i className="ri-weibo-line text-lg text-secondary-600"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-1">
                  官方微博
                </h3>
                <p className="text-xs text-foreground-500">
                  @地址发布页官方
                </p>
              </div>
            </div>

            <form
              onSubmit={handleSubmit}
              data-readdy-form=""
              className="bg-background-50 rounded-xl border border-background-200 p-6 md:p-8"
            >
              <h3 className="font-heading font-semibold text-foreground-900 text-base mb-6">
                发送消息
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    姓名
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-foreground-900 text-sm placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    邮箱
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-foreground-900 text-sm placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all"
                    placeholder="请输入您的邮箱地址"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label
                  htmlFor="subject"
                  className="block text-sm font-medium text-foreground-700 mb-1.5"
                >
                  主题
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-foreground-900 text-sm placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all"
                  placeholder="请输入消息主题"
                />
              </div>

              <div className="mb-5">
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-foreground-700 mb-1.5"
                >
                  消息内容
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-foreground-900 text-sm placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all resize-none"
                  placeholder="请输入您的消息内容（最多500字）"
                ></textarea>
                <p className="text-right text-xs text-foreground-400 mt-1">最多500字</p>
              </div>

              <div className="hp-field-wrap">
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              {formState === 'error' && formError && (
                <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                  <i className="ri-error-warning-line mr-1.5"></i>
                  {formError}
                </div>
              )}

              {formState === 'success' && (
                <div className="mb-4 p-3 rounded-lg bg-accent-50 border border-accent-200 text-accent-700 text-sm">
                  <i className="ri-check-line mr-1.5"></i>
                  消息已发送成功，我们会尽快回复您！
                </div>
              )}

              <button
                type="submit"
                disabled={formState === 'submitting'}
                className="w-full sm:w-auto bg-primary-500 hover:bg-primary-600 disabled:bg-primary-400 disabled:cursor-not-allowed text-background-50 px-6 py-2.5 rounded-lg font-semibold text-sm transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                {formState === 'submitting' ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-1.5"></i>
                    发送中...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line mr-1.5"></i>
                    发送消息
                  </>
                )}
              </button>
            </form>
          </div>
        </section>
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于我们 - 地址发布页',
            description: '官方地址发布页品牌介绍、口碑信誉与联系方式',
            url: 'https://gtucxr.cn/about',
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((faq) => ({
              '@type': 'Question',
              name: faq.q,
              acceptedAnswer: {
                '@type': 'Answer',
                text: faq.a,
              },
            })),
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '地址发布页',
            url: 'https://gtucxr.cn/',
            description: '官方地址发布页，提供最新可用访问入口与备用地址导航',
            contactPoint: [
              {
                '@type': 'ContactPoint',
                contactType: 'customer service',
                email: 'support@official-site.com',
                availableLanguage: ['Chinese'],
              },
            ],
            sameAs: [
              'https://weibo.com/address-page',
              'https://weixin.qq.com/address-page',
            ],
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://gtucxr.cn/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: '关于我们',
                item: 'https://gtucxr.cn/about',
              },
            ],
          }),
        }}
      />
    </Layout>
  );
}